import DOMPurify from 'dompurify';

/**
 * Mermaid 다이어그램을 **신뢰할 수 없는 입력**에서 안전하게 렌더하는 공용 모듈.
 *
 * ⚠️ 브라우저 전용이다. Electron `main`/`preload`나 Node 서버 코드에서 import하지 않는다 —
 * mermaid는 DOM(document/window)을 요구하고, DOMPurify도 window 없이는 동작하지 않는다.
 * 소비자는 web(Next 클라이언트 컴포넌트)과 desktop renderer 프로세스뿐이다.
 *
 * 이 모듈이 지키는 계약:
 * - mermaid는 **동적 import**로만 불러온다(초기 번들에서 분리, CDN·원격 스크립트 금지).
 * - `securityLevel: 'strict'`, `htmlLabels: false`로만 초기화한다.
 * - 입력의 `%%{init}%%` directive를 제거해 호출자가 정한 정책을 문서가 덮지 못하게 한다.
 * - 크기·복잡도 상한을 넘는 입력은 렌더 시도 자체를 하지 않는다.
 * - mermaid가 예외 대신 돌려주는 기본 "오류 SVG"를 성공으로 취급하지 않는다.
 * - 성공 결과도 DOMPurify로 한 번 더 살균한 SVG만 반환한다.
 */

/**
 * 공개 API는 각각 `contract.d.ts`의 선언에 `@type`으로 묶어 둔다. `pnpm typecheck`(checkJs)가
 * 구현과 선언이 벌어진 순간 실패하므로, 수작업 d.ts가 조용히 낡는 일을 막는다.
 * (`index.d.ts`를 참조하면 안 된다 — 같은 basename의 이 파일로 되돌아와 순환이 된다.)
 *
 * @typedef {import('./contract.d.ts').MermaidRenderResult} MermaidRenderResult
 * @typedef {import('./contract.d.ts')} MermaidRendererContract
 */

/**
 * 이 모듈이 실제로 쓰는 mermaid API 표면만 좁혀 둔다. mermaid의 전체 타입을 끌어오면
 * 버전마다 흔들리는 넓은 표면에 이 파일이 묶인다.
 *
 * @typedef {object} MermaidApi
 * @property {(config: Record<string, unknown>) => void} initialize
 * @property {(id: string, source: string) => Promise<{ svg?: unknown }>} render
 * @property {(source: string) => unknown} [parse]
 */

/** 기본 입력 상한. 호출자가 더 좁힐 수 있지만 넓히는 쪽으로 쓰지 않는 것을 전제한다. */
export const MERMAID_RENDER_LIMITS = Object.freeze({
  maxChars: 10_000,
  maxEdges: 400,
  timeoutMs: 3_000,
});

/**
 * theme별 엔트리가 같은 캐시를 공유한다(키에 theme이 들어 있다). 다크 모드를 왕복해도
 * 양쪽 theme의 결과가 함께 살아남을 만큼 여유를 둔다.
 */
const MAX_CACHE_ENTRIES = 64;

/**
 * mermaid는 render()에 넘긴 id를 SVG 루트 요소와 내부 `<style>` 스코프에 함께 새긴다.
 * 캐시된 문자열을 그대로 다시 삽입하면 같은 id를 가진 요소와 같은 style 블록이 문서에
 * 여러 벌 생겨 id 유일성이 깨진다. 그래서 캐시에는 id 자리를 토큰으로 치환한 템플릿을
 * 저장하고, 꺼낼 때마다 새 id를 채워 돌려준다.
 */
const CACHE_ID_TOKEN = '__mermaid_render_id__';

/**
 * mermaid 자신의 탐지 정규식(`/%{2}{\s*(?:(\w+)\s*:|(\w+)).../gi`)에는 앵커가 없어 들여쓴
 * directive든 줄 중간 directive든 위치와 무관하게 적용된다. 여기서 `^`/`$`로 줄 전체만
 * 노리면 탐지 범위가 mermaid보다 좁아져 방어층이 그대로 새므로, 앵커를 두지 않는다.
 */
const initDirectiveRegex = /%%\{[\s\S]*?\}%%/g;

/** @type {MermaidRendererContract['stripMermaidDirectives']} */
export const stripMermaidDirectives = (source) => {
  return String(source ?? '')
    .replace(initDirectiveRegex, '')
    .trim();
};

/** @type {MermaidRendererContract['estimateMermaidComplexity']} */
export const estimateMermaidComplexity = (source) => {
  const normalized = String(source ?? '').replace(/\r\n/g, '\n');
  const edgeCount = (normalized.match(/-->|---|==>|<--/g) ?? []).length;
  return {
    edgeCount,
    charCount: normalized.length,
  };
};

/** @type {MermaidRendererContract['shouldSkipMermaidRender']} */
export const shouldSkipMermaidRender = (source, options) => {
  const { maxChars = MERMAID_RENDER_LIMITS.maxChars, maxEdges = MERMAID_RENDER_LIMITS.maxEdges } = options ?? {};
  const stripped = stripMermaidDirectives(source);
  const { charCount, edgeCount } = estimateMermaidComplexity(stripped);

  if (charCount === 0) {
    return { skip: true, reason: 'empty' };
  }

  if (charCount > maxChars) {
    return { skip: true, reason: 'too_large' };
  }

  if (edgeCount > maxEdges) {
    return { skip: true, reason: 'too_complex' };
  }

  return { skip: false, reason: 'ok' };
};

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
const isRecord = (value) => typeof value === 'object' && value !== null;

/**
 * @param {unknown} value
 * @returns {value is MermaidApi}
 */
const isMermaidApi = (value) => {
  if (!isRecord(value)) {
    return false;
  }
  return typeof value.initialize === 'function' && typeof value.render === 'function';
};

const sanitizeSvg = (svg) => {
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    // `foreignObject`는 SVG 안에 임의 HTML을 되살리는 통로다. `htmlLabels: false`로 생성 자체를
    // 막지만, 살균 단계에서도 한 번 더 끊는다. `a`는 남겨도 이동할 수 없게 되므로 태그째 제거한다.
    FORBID_TAGS: ['script', 'foreignObject', 'iframe', 'object', 'embed', 'a'],
    FORBID_ATTR: [
      'onload',
      'onerror',
      'onclick',
      'ondblclick',
      'onmouseover',
      'onmouseenter',
      'onmouseleave',
      'href',
      'xlink:href',
    ],
  });
};

const looksLikeMermaidErrorSvg = (svg) => {
  // Mermaid의 기본 오류 SVG("폭탄" 아이콘 + 버전 워터마크)는 throw가 아니라 render()의 정상
  // 반환값으로 나온다. 보이는 메시지는 오류 종류마다 달라서 문구만으로는 판정할 수 없으므로
  // error role과 버전 워터마크(정상 다이어그램에는 없다)도 함께 본다.
  // `.error-icon`/`.error-text`만으로 판정하면 안 된다 — 정상 다이어그램의 공용 style 블록에도 들어 있다.
  const errorMarkers = ['aria-roledescription="error"', 'Syntax error in text', 'Lexical error in text', 'Parse error'];
  if (errorMarkers.some((marker) => svg.includes(marker))) {
    return true;
  }
  return /mermaid version\s+\d/i.test(svg);
};

/** 프로세스 수명 동안 단조 증가한다. 같은 소스를 다시 삽입할 때마다 새 DOM id를 얻는다. */
let renderSequence = 0;

const nextRenderId = (cacheKey) => {
  renderSequence += 1;
  return `m-${cacheKey}-${renderSequence.toString(36)}`;
};

const toCacheTemplate = (svg, id) => svg.split(id).join(CACHE_ID_TOKEN);

const fillCacheTemplate = (template, id) => template.split(CACHE_ID_TOKEN).join(id);

const fnv1aHashForCacheKey = (input) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
};

/** @type {MermaidApi | null} */
let cachedMermaid = null;
/** @type {string | null} */
let activeTheme = null;

const renderCache = new Map();
/** @type {string[]} */
const renderCacheKeys = [];

/**
 * parse/render는 mermaid 전역 config를 공유한다. theme이 다른 두 요청이 겹치면 뒤 요청의
 * `initialize`가 앞 요청의 render 도중에 config를 바꿔 잘못된 테마 SVG가 나온다.
 * 그래서 초기화~render 구간 전체를 한 줄로 세운다. 앞 요청이 매달려도 timeout이 풀어 준다.
 *
 * ⚠️ 의도된 trade-off: 이 직렬화 때문에 **동시에 요청된 다이어그램은 병렬로 그려지지 않는다**.
 * 한 화면에 다이어그램이 여러 개면 마지막 것의 표시 지연이 앞선 렌더 시간의 합이 되고,
 * 앞쪽 하나가 timeout(기본 3초)을 소진하면 그 뒤 전부가 그만큼 밀린다. 캐시 히트는 체인을
 * 거치지 않으므로 두 번째 표시부터는 즉시다. 성능 문제로 보인다면 큐가 아니라
 * "theme 전환만 직렬화하고 같은 theme은 병렬 허용"으로 범위를 좁히는 쪽을 검토한다 —
 * 큐를 그냥 없애면 theme 경합이 되살아난다.
 */
let renderChain = Promise.resolve();

/** @returns {Promise<MermaidApi>} */
const loadMermaid = async () => {
  if (cachedMermaid) {
    return cachedMermaid;
  }

  const mod = await import('mermaid');
  const candidate = isRecord(mod) && 'default' in mod ? mod.default : mod;

  if (!isMermaidApi(candidate)) {
    throw new Error('Mermaid module loaded but did not match the expected API shape.');
  }

  cachedMermaid = candidate;
  return candidate;
};

const normalizeTheme = (theme) => (typeof theme === 'string' && theme.length > 0 ? theme : 'default');

const ensureMermaidInitialized = async (theme) => {
  const mermaid = await loadMermaid();

  if (activeTheme !== theme) {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme,
      htmlLabels: false,
    });
    activeTheme = theme;

    // 캐시는 비우지 않는다. 키에 theme이 들어 있어(`${theme}:${cleaned}`) 색이 어긋난 SVG가
    // 재사용될 경로가 없고, 비워 버리면 다크 모드를 왕복할 때마다 화면의 모든 다이어그램이
    // 전량 재렌더된다. theme별 엔트리는 MAX_CACHE_ENTRIES LRU로 자연히 정리된다.
  }

  return mermaid;
};

const rememberRender = (cacheKey, svg) => {
  renderCache.set(cacheKey, svg);
  renderCacheKeys.push(cacheKey);
  if (renderCacheKeys.length > MAX_CACHE_ENTRIES) {
    const removed = renderCacheKeys.shift();
    if (typeof removed === 'string') {
      renderCache.delete(removed);
    }
  }
};

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} timeoutMs
 * @returns {Promise<T>}
 */
const withTimeout = async (promise, timeoutMs) => {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  try {
    return await Promise.race([
      promise,
      /** @type {Promise<T>} */ (
        new Promise((_resolve, reject) => {
          timer = setTimeout(() => reject(new Error('mermaid_render_timeout')), timeoutMs);
        })
      ),
    ]);
  } finally {
    // 타이머를 그냥 두면 렌더가 성공해도 timeoutMs 동안 이벤트 루프에 남는다.
    if (timer !== undefined) clearTimeout(timer);
  }
};

/**
 * @param {string} cleaned
 * @param {string} theme
 * @param {string} cacheKey
 * @param {number} timeoutMs
 * @returns {Promise<MermaidRenderResult>}
 */
const renderOnce = async (cleaned, theme, cacheKey, timeoutMs) => {
  /** @type {MermaidApi} */
  let mermaid;
  try {
    mermaid = await ensureMermaidInitialized(theme);
  } catch {
    return { ok: false, reason: 'load_error' };
  }

  try {
    if (typeof mermaid.parse === 'function') {
      // Mermaid 11의 parse()는 async다. 반환 promise를 await하지 않으면 rejection이 흘러가고
      // render()가 그대로 실행돼 기본 오류 SVG가 나온다. Promise.resolve는 동기 throw도 덮는다.
      await Promise.resolve(mermaid.parse(cleaned));
    }
  } catch {
    return { ok: false, reason: 'parse_error' };
  }

  const id = nextRenderId(cacheKey);

  try {
    const { svg } = await withTimeout(mermaid.render(id, cleaned), timeoutMs);
    if (typeof svg !== 'string' || svg.length === 0) {
      return { ok: false, reason: 'render_error' };
    }
    if (looksLikeMermaidErrorSvg(svg)) {
      return { ok: false, reason: 'syntax_error' };
    }

    const sanitized = sanitizeSvg(svg);
    rememberRender(cacheKey, toCacheTemplate(sanitized, id));
    return { ok: true, svg: sanitized, fromCache: false };
  } catch (error) {
    const reason = error instanceof Error && error.message === 'mermaid_render_timeout' ? 'timeout' : 'render_error';
    return { ok: false, reason };
  }
};

/**
 * Mermaid 소스를 살균된 SVG 문자열로 렌더한다.
 *
 * 실패는 예외가 아니라 `{ ok: false, reason }`으로 돌려준다 — 호출자가 원본 코드 블록을
 * 그대로 두는 fallback을 취할 수 있어야 하고, 실패했다고 화면이 비면 안 되기 때문이다.
 *
 * @type {MermaidRendererContract['renderMermaidDiagram']}
 */
export const renderMermaidDiagram = async (source, options) => {
  const gate = shouldSkipMermaidRender(source, options);
  if (gate.skip) {
    return { ok: false, reason: gate.reason };
  }

  const theme = normalizeTheme(options?.theme);
  const timeoutMs = options?.timeoutMs ?? MERMAID_RENDER_LIMITS.timeoutMs;
  const cleaned = stripMermaidDirectives(source);
  const cacheKey = fnv1aHashForCacheKey(`${theme}:${cleaned}`);

  const cached = renderCache.get(cacheKey);
  if (typeof cached === 'string') {
    // 같은 다이어그램을 한 화면에 두 번 넣어도 DOM id가 겹치지 않게 매번 새 id를 채운다.
    return { ok: true, svg: fillCacheTemplate(cached, nextRenderId(cacheKey)), fromCache: true };
  }

  const queued = renderChain.then(() => renderOnce(cleaned, theme, cacheKey, timeoutMs));
  // 체인은 끊기지 않아야 한다. 앞 작업의 실패가 뒤 작업을 막지 않도록 rejection을 흘려보낸다.
  renderChain = queued.then(
    () => undefined,
    () => undefined,
  );
  return queued;
};

/**
 * 테스트 전용. 모듈 전역(캐시·초기화된 theme·mermaid 인스턴스)을 초기 상태로 되돌린다.
 * 제품 코드에서는 호출하지 않는다.
 *
 * @type {MermaidRendererContract['__resetMermaidRendererState']}
 */
export const __resetMermaidRendererState = () => {
  cachedMermaid = null;
  activeTheme = null;
  renderCache.clear();
  renderCacheKeys.length = 0;
  renderChain = Promise.resolve();
};
